import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle } from 'lucide-react'
import type { CaseQuotaStatus } from '../../types/case-management'
import {
  formatBytes,
  calculateQuotaPercentage,
  formatQuotaDenialReason,
} from '../../hooks/use-case-quota'

interface CaseQuotaIndicatorProps {
  quota: CaseQuotaStatus
  showVolume?: boolean
}

function VolumeQuota({ quota }: { quota: CaseQuotaStatus }): ReactElement | null {
  const { t } = useTranslation('cases')
  if (!quota.seatVolumeLimitBytes || quota.volumeRemainingBytes === undefined) return null
  return (
    <>
      <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end">
          <span className="text-xs font-medium text-slate-600 dark:text-slate-400">
            {formatBytes(quota.volumeRemainingBytes)}
          </span>
          <span className="text-[10px] text-slate-500 dark:text-slate-500">
            {t('quota.storageRemaining')}
          </span>
        </div>
        <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className="h-full rounded-full bg-violet-500 transition-all"
            style={{
              width: `${calculateQuotaPercentage(
                quota.totalVolumeUsedBytes,
                quota.seatVolumeLimitBytes
              ).toString()}%`,
            }}
          />
        </div>
      </div>
    </>
  )
}

export function CaseQuotaIndicator({
  quota,
  showVolume = false,
}: CaseQuotaIndicatorProps): ReactElement {
  const { t } = useTranslation('cases')
  const casesUsed = quota.casesPerSeatLimit - quota.casesRemaining
  const casePercentage = calculateQuotaPercentage(casesUsed, quota.casesPerSeatLimit)

  const isLow = quota.casesRemaining <= 2
  const isAtLimit = quota.casesRemaining === 0

  const getBarColour = (): string => {
    if (isAtLimit) return 'bg-red-500'
    if (isLow) return 'bg-amber-500'
    return 'bg-lime-500'
  }

  const getTextColour = (): string => {
    if (isAtLimit) return 'text-red-600 dark:text-red-400'
    if (isLow) return 'text-amber-600 dark:text-amber-400'
    return 'text-slate-600 dark:text-slate-400'
  }

  if (!quota.canCreateCase) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-100 px-3 py-1.5 dark:border-red-800 dark:bg-red-950/50">
        <AlertCircle className="h-4 w-4 text-red-500" />
        <span className="text-xs font-medium text-red-700 dark:text-red-300">
          {formatQuotaDenialReason(quota.denialReason)}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <div className="flex flex-col items-end">
          <span className={`text-xs font-medium ${getTextColour()}`}>
            {quota.casesRemaining} of {quota.casesPerSeatLimit}
          </span>
          <span className="text-[10px] text-slate-500 dark:text-slate-500">
            {t('quota.casesRemaining')}
          </span>
        </div>
        <div className="h-2 w-16 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className={`h-full rounded-full transition-all ${getBarColour()}`}
            style={{ width: `${casePercentage.toString()}%` }}
          />
        </div>
      </div>
      {showVolume && <VolumeQuota quota={quota} />}
      {quota.casesRemaining > 2 && <CheckCircle className="h-4 w-4 text-green-500" />}
    </div>
  )
}
